/**
 * ImpersonationBanner — bandeau rouge persistant du portail quand l'admin « navigue en tant que » un client :
 * nom du client, mode (lecture seule / test), compte à rebours (15 min) et « Revenir à l'administration » — Batch #14.338.
 */
import { useEffect, useState } from "react";
import { ShieldAlert, LogOut, FlaskConical, Eye } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";

const fmt = (ms) => {
  const s = Math.max(0, Math.floor(ms / 1000));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;
};

export default function ImpersonationBanner({ client, info }) {
  const { logoutPortal } = useAuth();
  const [left, setLeft] = useState(() => (info?.expires_at ? new Date(info.expires_at) - Date.now() : 0));

  useEffect(() => {
    if (!info?.expires_at) return undefined;
    const end = new Date(info.expires_at).getTime();
    const t = setInterval(() => setLeft(end - Date.now()), 1000);
    return () => clearInterval(t);
  }, [info?.expires_at]);

  const [leaving, setLeaving] = useState(false);
  const leave = async () => {
    if (leaving) return;
    setLeaving(true);
    await logoutPortal(); // clôt la session côté serveur (journal) avant de quitter
    // onglet ouvert par l'admin : on le ferme ; sinon retour à l'administration
    window.close();
    setTimeout(() => { if (!window.closed) window.location.href = "/admin/portail-clients"; }, 150);
  };

  useEffect(() => { if (info?.expires_at && left <= 0) leave(); }, [left]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!info) return null;
  const name = `${client?.first_name || ""} ${client?.last_name || ""}`.trim() || client?.email || "ce client";
  const test = info.mode === "test";
  return (
    <div className={`${test ? "bg-amber-600" : "bg-red-700"} text-white sticky top-0 z-[60] shadow-md`} data-testid="impersonation-banner" role="alert">
      <div className="max-w-6xl mx-auto px-3 py-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm">
        <span className="inline-flex items-center gap-2 font-bold"><ShieldAlert className="w-4 h-4" /> Mode Admin</span>
        <span data-testid="impersonation-banner-client">Vous naviguez en tant que <strong>{name}</strong></span>
        <span className="inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded-full bg-white/20" data-testid="impersonation-banner-mode">
          {test ? <><FlaskConical className="w-3 h-3" /> Mode test — actions critiques bloquées</> : <><Eye className="w-3 h-3" /> Lecture seule</>}
        </span>
        <span className="font-mono text-xs opacity-90" data-testid="impersonation-banner-timer" title="Fermeture automatique à l'expiration">expire dans {fmt(left)}</span>
        <button onClick={leave} disabled={leaving} className="ml-auto inline-flex items-center gap-1.5 bg-white text-red-800 hover:bg-red-50 disabled:opacity-60 font-semibold text-xs px-3 py-1.5 rounded-lg" data-testid="impersonation-banner-leave">
          <LogOut className="w-3.5 h-3.5" /> Revenir à l'administration
        </button>
      </div>
    </div>
  );
}
